import { useMemo, useState } from "react"
import { createPortal } from "react-dom"
import { format } from "date-fns"
import { ChevronLeft, ChevronRight, RefreshCw, X } from "lucide-react"
import { Button } from "@/components/ui/button"
import { driverRoomJid } from "@/jidUtils"
import { useDriverDisplayName } from "./useDriverDisplayName"
import { useDriverPlate } from "./useDriverPlate"
import { useFuelReportData } from "./useFuelReportData"
import { useDayNavigation } from "./useDayNavigation"
import { FuelTimelineChip } from "./FuelTimelineChip"
import { driverChipEvents } from "./fuelReportSummary"
import { FuelEventDetailDialog } from "./FuelEventDetailDialog"
import { useTranslation } from "react-i18next"
import { getDateFnsLocale } from "@/lib/i18n/format"

export function ChatFuelReportDialog({
  driverJid,
  chatJid,
  open,
  onClose,
}: {
  driverJid: string
  chatJid: string
  open: boolean
  onClose: () => void
}) {
  const { t } = useTranslation()
  const {
    selectedDate,
    setSelectedDate,
    goToPreviousDay,
    goToNextDay,
    isToday,
    dayWindow,
  } = useDayNavigation()
  // The chat header hands us the bare `local@<vhost>` driver JID, but the
  // fuel tables key drivers by the room form `local@drivers.<vhost>`. Query
  // with that form so the per-driver drawer isn't empty.
  const queryDriverJid = useMemo(() => driverRoomJid(driverJid), [driverJid])
  const { drivers, isLoading, error, refetch } = useFuelReportData(
    useMemo(
      () => ({ from: selectedDate, to: selectedDate }),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [format(selectedDate, "yyyy-MM-dd")],
    ),
    queryDriverJid,
  )
  const [detail, setDetail] = useState<{
    eventKind: "alert" | "refuel"
    eventId: string
    at: string
    window: { from: string; to: string }
    partnerAt?: string
    suppressed?: boolean
    reason?: string | null
  } | null>(null)

  const driver = drivers[0]
  // Same refuel-only, verdict-colored timeline as the main report list.
  const chips = useMemo(
    () => (driver ? driverChipEvents(driver) : []),
    [driver],
  )
  // Resolve the real display name (chat/profile), not a mangled JID localpart.
  const driverName = useDriverDisplayName(driverJid, chatJid)
  const driverPlate = useDriverPlate(driverJid, chatJid)

  if (!open) return null

  return (
    <>
      {createPortal(
        // z-[1000] matches the app's Dialog overlay — the chat map panel and
        // its popups sit above plain z-50.
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50">
          <div className="relative flex h-[85vh] w-[90vw] max-w-3xl flex-col rounded-lg bg-background shadow-xl">
            <div className="flex items-center justify-between border-b px-4 py-3">
              <h2 className="text-lg font-semibold">
                {t("reports.chatFuelReport.title")}
              </h2>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="icon-sm"
                  onClick={goToPreviousDay}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="min-w-[140px] text-center text-sm font-medium">
                  {format(selectedDate, "dd MMMM yyyy", {
                    locale: getDateFnsLocale(),
                  })}
                </span>
                <Button
                  variant="outline"
                  size="icon-sm"
                  onClick={goToNextDay}
                  disabled={isToday}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
                {!isToday && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedDate(new Date())}
                  >
                    {t("common.today")}
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={refetch}
                  disabled={isLoading}
                >
                  <RefreshCw
                    className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`}
                  />
                </Button>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={onClose}
                  aria-label={t("common.close")}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="flex-1 overflow-auto p-4">
              {isLoading && (
                <div className="text-muted-foreground">
                  {t("reports.shared.loading")}
                </div>
              )}
              {error && (
                <div className="text-red-500">
                  {t("reports.shared.error", { error })}
                </div>
              )}
              {!isLoading && !error && (
                <div className="flex flex-col gap-4">
                  <div className="flex gap-4 text-xs text-muted-foreground">
                    <span>
                      <span className="mr-1 inline-block h-2 w-2 rounded-full bg-green-500" />
                      {t("reports.fuelLegend.matched")}
                    </span>
                    <span>
                      <span className="mr-1 inline-block h-2 w-2 rounded-full bg-red-500" />
                      {t("reports.fuelLegend.elsewhere")}
                    </span>
                    <span>
                      <span className="mr-1 inline-block h-2 w-2 rounded-full bg-gray-400" />
                      {t("reports.fuelLegend.noAlert")}
                    </span>
                  </div>
                  <section>
                    <h3 className="mb-2 text-sm font-semibold">
                      {t("reports.chatFuelReport.refuels", {
                        number: chips.length,
                      })}
                    </h3>
                    {chips.length === 0 ? (
                      <div className="py-8 text-center text-muted-foreground">
                        {t("reports.chatFuelReport.empty")}
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-1.5">
                        {chips.map((chip) => {
                          const r = chip.refuel
                          const linkedAlert = r.linkedAlertId
                            ? driver?.alerts.find(
                                (a) => a.id === r.linkedAlertId,
                              )
                            : undefined
                          return (
                            <FuelTimelineChip
                              key={r.id}
                              event={chip}
                              showDate={false}
                              onClick={() =>
                                setDetail({
                                  eventKind: "refuel",
                                  // Query by the linked alert when there is
                                  // one — only the alert side carries the
                                  // "seen" status, and its payload already
                                  // includes this refuel.
                                  eventId: r.linkedAlertId ?? r.id,
                                  at: r.refueledAt,
                                  window: dayWindow,
                                  partnerAt: linkedAlert?.sentAt,
                                  suppressed: linkedAlert?.suppressed,
                                  reason: linkedAlert?.reason,
                                })
                              }
                            />
                          )
                        })}
                      </div>
                    )}
                  </section>
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body,
      )}
      {detail && (
        <FuelEventDetailDialog
          open
          driverJid={queryDriverJid}
          driverName={driverName}
          driverPlate={driverPlate}
          eventKind={detail.eventKind}
          eventId={detail.eventId}
          eventAt={detail.at}
          window={detail.window}
          partnerAt={detail.partnerAt}
          suppressed={detail.suppressed}
          reason={detail.reason}
          onOpenChange={(o) => !o && setDetail(null)}
        />
      )}
    </>
  )
}
